import { EVENT_MODE_NORMAL, EVENT_MODE_PUNCHUP } from 'utils/constants';
import {
	EVENT_SCANS,
	LOTTERY,
	NATIVE_DASHBOARD_HIGHLIGHTS,
	PAGE_VIEWS,
	REG_DASHBOARD_HIGHLIGHTS,
	TICKETS_COMPED,
	TICKETS_SOLD,
	TOTAL_ATTENDEES,
	TOTAL_PAYOUT,
} from 'features/common/components/HighlightsCollection/constants';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { BoxProps } from 'features/common/components/HighlightsCollection/types';
import { ContentWrapper } from 'features/Home/components/Highlights/Highlights.styles';
import { EventData } from 'features/common/types';
import { IdParamsType } from 'utils/types';
import { PunchupHighlightsCollection } from 'features/common/components/Form/PunchupHighlightsCollection';
import { ROUTES } from 'features/routes';
import { toSigDigits } from 'utils/utils';
import { useHighlights } from 'hooks/useHighlights';

const PunchupHighlights = ({ event }: { event: EventData }) => {
	const { push } = useHistory();
	const { id } = useParams<IdParamsType>();
	const { eventHighlights } = useHighlights(id);

	// Iframe-embedded tixologist dashboard is red zone — its tiles must match the native standalone
	// view (NATIVE_DASHBOARD_HIGHLIGHTS), not the analytics-led green-zone set. See NORMAL vs NATIVE
	// note in HighlightsCollection/constants.
	const initialBoxCollection = [EVENT_MODE_NORMAL, EVENT_MODE_PUNCHUP].includes(event.mode)
		? NATIVE_DASHBOARD_HIGHLIGHTS
		: REG_DASHBOARD_HIGHLIGHTS;

	const [boxCollection, setBoxCollection] = useState(initialBoxCollection);

	useEffect(() => {
		const boxCollections: BoxProps[] = [];
		for (let i = 0; i < boxCollection.length; i++) {
			let box: BoxProps | undefined = boxCollection[i];
			box.isLoading = !eventHighlights;
			if (box.property === TOTAL_PAYOUT) {
				box.stat = toSigDigits(eventHighlights?.total_payout || 0);
				box.buttonDisabled = !eventHighlights?.total_payout;
				box.onClick = () => push(generatePath(ROUTES.ORDERS_BY_EVENT_PATH, { id }));
			} else if (box.property === TICKETS_SOLD) {
				box.stat = eventHighlights?.tickets_sold || 0;
				box.buttonDisabled = !eventHighlights?.tickets_sold;
				box.onClick = () => push(generatePath(ROUTES.ANALYTICS_BY_EVENT_PATH, { id }));
			} else if (box.property === TOTAL_ATTENDEES) {
				box.stat = eventHighlights?.total_attendees || 0;
				box.onClick = () => push(generatePath(ROUTES.ATTENDEES, { id }));
			} else if (box.property === PAGE_VIEWS) {
				box.stat = eventHighlights?.page_views || 0;
				box.buttonDisabled = !eventHighlights?.page_views;
				box.onClick = () => push(generatePath(ROUTES.ANALYTICS_BY_EVENT_PATH, { id }));
			} else if (box.property === TICKETS_COMPED) {
				box.stat = eventHighlights?.tickets_comped || 0;
				box.buttonDisabled = !event.ticketTypes.length;
				box.onClick = () => push(generatePath(ROUTES.COMPED_TICKETS, { id }));
			} else if (box.property === LOTTERY) {
				if (event.ticketTypes.some((ticket) => ticket.isLottery)) {
					box.stat = eventHighlights?.lottery_entries || 0;
					box.buttonDisabled = !eventHighlights?.lottery_entries;
					box.onClick = () => push(generatePath(ROUTES.LOTTERY, { id }));
				} else {
					box = undefined;
				}
			} else if (box.property === EVENT_SCANS) {
				const scansCount = eventHighlights?.nums_of_scans || 0;
				const totalAttendees = eventHighlights?.total_attendees || 0;
				const percentage = totalAttendees > 0 ? Math.round((scansCount / totalAttendees) * 100) : 0;
				box.stat = scansCount;
				box.subStat = `${percentage}%`;
				// No onClick — the scanner web app is not offered to customers from the iframe dashboard
				// (PUN-4942 req. 6), mirroring the green view (TIC-308). The tile reports the count only;
				// /scans stays routable for direct/internal access. Cleared explicitly rather than just
				// omitted because the cards in NATIVE_DASHBOARD_HIGHLIGHTS are shared module singletons
				// that other dashboards mutate in place.
				box.onClick = undefined;
				box.buttonDisabled = undefined;
			}
			box && boxCollections.push(box);
		}
		setBoxCollection(boxCollections);
	}, [eventHighlights, event?.ticketTypes]);

	return (
		<ContentWrapper>
			<PunchupHighlightsCollection boxCollection={boxCollection} />
		</ContentWrapper>
	);
};

export { PunchupHighlights };
